import { useCallback, useEffect, useRef, useState } from 'react';
import { useAudioPlayer } from '@contexts/AudioPlayerContext';
import { formatTime, parseDuration, parseTime } from '@utils/time/timeUtils';
import { downloadAudioClip } from '@utils/audio/clipExporter';
import {
  estimateClipSize,
  validateClipRequest
} from '@models/audio/audioModel';

export function useClipExportController({ episode, isMobile, onClose }) {
  const { currentTime, duration } = useAudioPlayer();
  const [startTime, setStartTime] = useState('0:00');
  const [endTime, setEndTime] = useState('1:00');
  const [error, setError] = useState('');
  const [exporting, setExporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState('');
  const closeTimeoutRef = useRef(null);

  useEffect(() => () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
    }
  }, []);

  const handleUseCurrentPosition = useCallback(() => {
    setStartTime(formatTime(currentTime));
  }, [currentTime]);

  const validate = useCallback(() => {
    const episodeDuration = parseDuration(episode?.duration || duration);
    return validateClipRequest({
      startTime,
      endTime,
      episodeDuration,
      isAudioContextSupported:
        typeof window !== 'undefined' &&
        Boolean(window.AudioContext || window.webkitAudioContext),
      isMobile
    });
  }, [duration, endTime, episode, isMobile, startTime]);

  const handleExport = useCallback(async () => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setError('');
    setExporting(true);
    setProgress(0);

    try {
      await downloadAudioClip({
        episodeUrl: episode.url,
        episodeTitle: episode.title,
        startTime: parseTime(startTime),
        endTime: parseTime(endTime),
        episodeDuration: parseDuration(episode.duration || duration),
        onProgress: (percent, message) => {
          setProgress(percent);
          setProgressMessage(message);
        }
      });

      closeTimeoutRef.current = setTimeout(() => {
        onClose();
        setExporting(false);
        setProgress(0);
      }, 500);
    } catch (exportError) {
      console.error('Clip export failed:', exportError);
      setError(exportError.message || 'Export failed. Please try again.');
      setExporting(false);
      setProgress(0);
    }
  }, [duration, endTime, episode, onClose, startTime, validate]);

  return {
    startTime,
    endTime,
    error,
    exporting,
    progress,
    progressMessage,
    estimatedSize: estimateClipSize(startTime, endTime),
    onStartTimeChange: setStartTime,
    onEndTimeChange: setEndTime,
    onUseCurrentPosition: handleUseCurrentPosition,
    onExport: handleExport
  };
}
